// POSCartCounterFooter - Counter mode footer with notes, discount, totals, and payment buttons
import React from "react";
import { Button } from "@/components/ui/button";
import { Tag } from "lucide-react";

export function POSCartCounterFooter({
  orderNotes,
  onNotesChange,
  subtotal,
  total,
  discountAmount,
  onDiscountChange,
  requireCustomer,
  customerName,
  onCustomerNameChange,
  customerPhone,
  onCustomerPhoneChange,
  showPaymentButtons,
  isTableOrder,
  processing,
  onCheckout,
  extraPaymentMethods,
}) {
  const [discountInput, setDiscountInput] = React.useState("");
  const [discountType, setDiscountType] = React.useState("fixed");
  const [discountReason, setDiscountReason] = React.useState("");

  return (
    <>
      {/* Observação para a cozinha */}
      <div>
        <label className="text-xs font-medium text-gray-500 mb-1 block">Observação</label>
        <textarea
          value={orderNotes}
          onChange={e => onNotesChange?.(e.target.value)}
          placeholder="Ex: sem cebola, alergia a amendoim..."
          rows={2}
          className="w-full text-sm rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 resize-none focus:outline-none focus:ring-1 focus:ring-emerald-400 placeholder:text-gray-300"
        />
      </div>

      {/* Desconto */}
      {onDiscountChange && (
        <div>
          <label className="text-xs font-medium text-gray-500 mb-1 flex items-center gap-1">
            <Tag className="w-3 h-3" /> Desconto
          </label>
          <div className="flex gap-1">
            <button
              onClick={() => { setDiscountType("fixed"); setDiscountInput(""); onDiscountChange({ amount: 0, type: "fixed", value: 0, reason: discountReason }); }}
              className={`text-xs px-2 py-1 rounded border font-semibold transition ${discountType === "fixed" ? "bg-emerald-100 border-emerald-400 text-emerald-700" : "border-gray-200 text-gray-500"}`}
            >€</button>
            <button
              onClick={() => { setDiscountType("percent"); setDiscountInput(""); onDiscountChange({ amount: 0, type: "percent", value: 0, reason: discountReason }); }}
              className={`text-xs px-2 py-1 rounded border font-semibold transition ${discountType === "percent" ? "bg-emerald-100 border-emerald-400 text-emerald-700" : "border-gray-200 text-gray-500"}`}
            >%</button>
            <input
              type="number"
              min="0"
              step="0.01"
              value={discountInput}
              onChange={e => {
                const v = e.target.value;
                setDiscountInput(v);
                const num = parseFloat(v) || 0;
                const computed = discountType === "percent"
                  ? Math.min(subtotal, subtotal * num / 100)
                  : Math.min(subtotal, num);
                onDiscountChange({ amount: Math.round(computed * 100) / 100, type: discountType, value: num, reason: discountReason });
              }}
              placeholder={discountType === "percent" ? "0 %" : "0.00 €"}
              className="flex-1 text-sm rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-emerald-400"
              data-testid="pos-discount-input"
            />
          </div>
          <input
            type="text"
            value={discountReason}
            onChange={e => {
              setDiscountReason(e.target.value);
              if (discountAmount > 0) {
                onDiscountChange({ amount: discountAmount, type: discountType, value: parseFloat(discountInput) || 0, reason: e.target.value });
              }
            }}
            placeholder="Motivo do desconto (opcional)"
            className="mt-1 w-full text-xs rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-emerald-400"
            data-testid="pos-discount-reason"
          />
          {discountAmount > 0 && (
            <p className="text-xs text-emerald-600 mt-0.5 font-medium">
              Desconto aplicado: -{discountAmount.toFixed(2)}€
            </p>
          )}
        </div>
      )}

      {/* Totais */}
      <div className="space-y-1">
        <div className="flex justify-between text-sm">
          <span className="text-gray-500">Subtotal</span>
          <span className="font-medium">{subtotal.toFixed(2)}€</span>
        </div>
        {discountAmount > 0 && (
          <div className="flex justify-between text-sm text-emerald-600" data-testid="pos-discount-line">
            <span>Desconto</span>
            <span>-{discountAmount.toFixed(2)}€</span>
          </div>
        )}
        <div className="flex justify-between text-lg font-bold">
          <span>Total</span>
          <span className="text-emerald-600" data-testid="pos-total">{(total ?? subtotal).toFixed(2)}€</span>
        </div>
      </div>

      {/* Cliente (Pedido Balcão) */}
      {requireCustomer && (
        <div className="space-y-2 border-t pt-2">
          <p className="text-xs font-bold text-gray-600 uppercase">Cliente</p>
          <input
            type="text"
            value={customerName}
            onChange={e => onCustomerNameChange?.(e.target.value)}
            placeholder="Nome do cliente *"
            className="w-full text-sm rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-emerald-400"
          />
          <input
            type="tel"
            value={customerPhone}
            onChange={e => onCustomerPhoneChange?.(e.target.value)}
            placeholder="Telemóvel (opcional — activa notificações)"
            className="w-full text-sm rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-emerald-400"
          />
        </div>
      )}

      {/* Botões de pagamento */}
      {showPaymentButtons && (
        <div className="grid gap-2 grid-cols-2">
          <Button
            onClick={() => onCheckout?.("cash")}
            disabled={processing}
            className="bg-amber-500 hover:bg-amber-600 text-white font-bold h-12"
            data-testid="checkout-cash"
          >
            💵 DINHEIRO
          </Button>
          <Button
            onClick={() => onCheckout?.("card")}
            disabled={processing}
            className="bg-blue-500 hover:bg-blue-600 text-white font-bold h-12"
            data-testid="checkout-card"
          >
            💳 CARTÃO
          </Button>
          {extraPaymentMethods.map((method, idx) => (
            <Button
              key={idx}
              onClick={() => onCheckout?.(method.toLowerCase().replace(/\s+/g, "_"))}
              disabled={processing}
              className="bg-purple-500 hover:bg-purple-600 text-white font-bold h-12 text-xs col-span-2"
              data-testid={`checkout-extra-${idx}`}
            >
              {method.toUpperCase()}
            </Button>
          ))}
        </div>
      )}

      {/* Botão para pedido de mesa */}
      {isTableOrder && (
        <Button
          onClick={() => onCheckout?.("table")}
          disabled={processing}
          className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-bold h-12"
          data-testid="checkout-table"
        >
          📤 ENVIAR PARA COZINHA
        </Button>
      )}
    </>
  );
}
